package com.mgacreative.hotel

import androidx.compose.foundation.layout.*
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.Menu
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.mgacreative.hotel.shared.ui.designsystem.theme.HotelTheme
import com.mgacreative.hotel.core.tenant.TenantSession
import com.mgacreative.hotel.core.tenant.SessionState
import com.mgacreative.hotel.shared.ui.designsystem.components.HFNavigationDrawer
import com.mgacreative.hotel.shared.ui.designsystem.components.HelpScreen
import com.mgacreative.hotel.shared.ui.designsystem.components.SubscriptionScreen
import com.mgacreative.hotel.shared.ui.designsystem.components.OnboardingDialog
import com.mgacreative.hotel.core.network.SupabaseNetworkClient
import io.github.jan.supabase.auth.auth
import kotlinx.coroutines.launch
import org.koin.compose.koinInject

// Feature UI Imports
import com.mgacreative.hotel.feature.admin.ui.AdminDashboardScreen
import com.mgacreative.hotel.feature.admin.ui.AdminMembersScreen
import com.mgacreative.hotel.feature.admin.ui.AdminMenuManagementScreen
import com.mgacreative.hotel.feature.admin.ui.HotelProfileScreen
import com.mgacreative.hotel.feature.admin.ui.RoomManagementScreen
import com.mgacreative.hotel.feature.admin.ui.FloorPlanEditorScreen
import com.mgacreative.hotel.feature.admin.ui.PricingManagementScreen
import com.mgacreative.hotel.feature.admin.ui.AdminSupplierManagementScreen
import com.mgacreative.hotel.feature.admin.ui.AdminAgencyManagementScreen
import com.mgacreative.hotel.feature.admin.ui.AgencyContractScreen
import com.mgacreative.hotel.feature.admin.ui.AdminDepartmentManagementScreen
import com.mgacreative.hotel.feature.admin.ui.AdminStaffManagementScreen
import com.mgacreative.hotel.feature.admin.ui.AdminBlacklistScreen
import com.mgacreative.hotel.feature.admin.ui.KbsSettingsScreen
import com.mgacreative.hotel.feature.hotel.ui.auth.AuthScreenContainer
import com.mgacreative.hotel.feature.reports.ui.DashboardScreen
import com.mgacreative.hotel.feature.reports.ui.StatisticsScreen
import com.mgacreative.hotel.feature.room.ui.InUseRoomsScreen
import com.mgacreative.hotel.feature.room.ui.RoomStatusScreen
import com.mgacreative.hotel.feature.room.ui.RoomSummaryScreen
import com.mgacreative.hotel.feature.reservation.ui.ReservationListScreen
import com.mgacreative.hotel.feature.reservation.ui.NewReservationCalendarScreen
import com.mgacreative.hotel.feature.reservation.ui.create.CreateReservationScreen
import com.mgacreative.hotel.feature.reservation.ui.CalendarScreen
import com.mgacreative.hotel.feature.reservation.ui.checkout.CheckOutListScreen
import com.mgacreative.hotel.feature.reservation.ui.checkin.CheckInListScreen
import com.mgacreative.hotel.feature.housekeeping.ui.TaskListScreen
import com.mgacreative.hotel.feature.staff.ui.StaffListScreen
import com.mgacreative.hotel.feature.staff.ui.ShiftPlanningScreen
import com.mgacreative.hotel.feature.finance.ui.CashRegisterScreen
import com.mgacreative.hotel.feature.finance.ui.ProfitReportScreen
import com.mgacreative.hotel.feature.finance.ui.CurrentAccountsScreen
import com.mgacreative.hotel.feature.restaurant.ui.RestaurantPosScreen
import com.mgacreative.hotel.feature.inventory.ui.InventoryScreen
import com.mgacreative.hotel.feature.maintenance.ui.MaintenanceListScreen
import com.mgacreative.hotel.feature.channelmanager.ui.ChannelManagerScreen
import com.mgacreative.hotel.feature.channelmanager.ui.AgencyAllotmentScreen

@Composable
fun WebApp() {
    HotelTheme {
        val tenantSession: TenantSession = koinInject()
        val sessionState by tenantSession.sessionState.collectAsState()
        
        Surface(modifier = Modifier.fillMaxSize(), color = MaterialTheme.colorScheme.background) {
            if (sessionState.isAuthenticated) {
                AppRootComponent(sessionState, tenantSession)
            } else {
                AuthScreenContainer()
            }
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun AppRootComponent(sessionState: SessionState, tenantSession: TenantSession) {
    val supabaseClient: SupabaseNetworkClient = koinInject()
    val drawerState = rememberDrawerState(initialValue = DrawerValue.Closed)
    val scope = rememberCoroutineScope()
    val backStack = remember { mutableStateListOf("DashboardScreen") }
    val currentScreen = backStack.lastOrNull() ?: "DashboardScreen"
    var showOnboarding by remember { mutableStateOf(false) }
    
    val popBackStack: () -> Unit = {
        if (backStack.size > 1) {
            backStack.removeAt(backStack.lastIndex)
        } else if (backStack.firstOrNull() != "DashboardScreen") {
            backStack.clear()
            backStack.add("DashboardScreen")
        }
    }
    val navigateTo: (String) -> Unit = { screen -> if (backStack.lastOrNull() != screen) backStack.add(screen) }

    val screensWithCustomTopBar = listOf(
        "RestaurantPosScreen",
        "AdminMenuManagementScreen",
        "HotelProfileScreen",
        "AdminStaffManagementScreen",
        "AdminSupplierManagementScreen",
        "AdminDepartmentManagementScreen",
        "AgencyContractScreen",
        "InventoryScreen",
        "CashRegisterScreen",
        "ProfitReportScreen",
        "CurrentAccountsScreen",
        "HousekeepingScreen",
        "MaintenanceScreen",
        "RoomSummaryScreen",
        "ShiftPlannerScreen",
        "StaffListScreen",
        "RoomStatusScreen",
        "InUseRoomsScreen",
        "CheckInListScreen",
        "ReservationCalendarScreen",
        "ChannelManagerScreen",
        "AgencyAllotmentScreen",
        "AdminAgencyManagementScreen",
        "RoomManagementScreen",
        "FloorPlanEditorScreen",
        "StatisticsScreen",
        "ReportsScreen"
    )
    
    @Composable
    fun getScreenTitle(screen: String): String {
        val turkishTitle = when (screen) {
            "DashboardScreen" -> "Gösterge Paneli"
            "StatisticsScreen" -> "İstatistikler & Raporlar"
            "ReportsScreen" -> "İstatistikler & Raporlar"
            "SubscriptionScreen" -> "HotelOS Gold"
            "InUseRoomsScreen" -> "Dolu Odalar"
            "RoomStatusScreen" -> "Oda Durumları"
            "AdminDashboardScreen" -> "Sistem Yönetimi"
            "AdminMembersScreen" -> "Üye Yönetimi"
            "HotelProfileScreen" -> "Otel Profili"
            "RoomManagementScreen" -> "Oda Yönetimi"
            "FloorPlanEditorScreen" -> "Kat Planı Düzenleyici"
            "AdminMenuManagementScreen" -> "Menü Yönetimi"
            "PricingManagementScreen" -> "Dinamik Fiyatlandırma"
            "AdminSupplierManagementScreen" -> "Tedarikçi Yönetimi"
            "AdminAgencyManagementScreen" -> "Acenta Yönetimi"
            "AgencyContractScreen" -> "Acenta Sözleşmeleri"
            "AdminDepartmentManagementScreen" -> "Departman Yönetimi"
            "AdminStaffManagementScreen" -> "Personel Yönetimi"
            "AdminBlacklistScreen" -> "Kara Liste"
            "KbsSettingsScreen" -> "KBS Ayarları"
            "MaintenanceScreen" -> "Bakım & Onarım"
            "ReservationListScreen" -> "Rezervasyonlar"
            "NewReservationCalendarScreen" -> "Ön Büro (Rezervasyon)"
            "CreateReservationScreen" -> "Yeni Rezervasyon"
            "ReservationCalendarScreen" -> "Takvim"
            "CheckOutListScreen" -> "Çıkış Yapacaklar (Check-Out)"
            "CheckInListScreen" -> "Giriş Yapacaklar (Check-In)"
            "HousekeepingScreen" -> "Kat Hizmetleri"
            "StaffListScreen" -> "Personel Listesi"
            "ShiftPlannerScreen" -> "Vardiya Planı"
            "CashRegisterScreen" -> "Kasa İşlemleri"
            "ProfitReportScreen" -> "Kâr / Zarar Raporu"
            "CurrentAccountsScreen" -> "Cari Hesaplar"
            "RestaurantPosScreen" -> "Restoran POS"
            "InventoryScreen" -> "Envanter Yönetimi"
            "HelpScreen" -> "Yardım & Destek"
            "RoomSummaryScreen" -> "Oda Özet"
            else -> screen
        }
        return com.mgacreative.hotel.shared.ui.designsystem.i18n.l10n(turkishTitle)
    }

    PermanentNavigationDrawer(
        drawerContent = {
            PermanentDrawerSheet(
                modifier = Modifier.width(300.dp),
                drawerContainerColor = MaterialTheme.colorScheme.surface
            ) {
                HFNavigationDrawer(
                    userRole = sessionState.role.name,
                    userRoles = sessionState.roles,
                    allowedModules = sessionState.allowedModules,
                    onLogoutClick = {
                        scope.launch {
                            try {
                                supabaseClient.client.auth.signOut()
                            } catch (_: Exception) {}
                            tenantSession.clearSession()
                        }
                    },
                    onNavigate = { screen ->
                        navigateTo(screen)
                    }
                )
            }
        }
    ) {
        Scaffold(
            topBar = {
                if (currentScreen !in screensWithCustomTopBar) {
                    CenterAlignedTopAppBar(
                        title = { Text(getScreenTitle(currentScreen), fontWeight = FontWeight.Bold) },
                        navigationIcon = {
                            if (currentScreen != "DashboardScreen") {
                                IconButton(onClick = popBackStack) { Icon(Icons.AutoMirrored.Filled.ArrowBack, "Geri") }
                            }
                        },
                        colors = TopAppBarDefaults.centerAlignedTopAppBarColors(
                            containerColor = Color.White,
                            titleContentColor = Color(0xFF0F172A),
                            navigationIconContentColor = Color(0xFF0F172A)
                        )
                    )
                }
            }
        ) { padding ->
            Box(modifier = Modifier.fillMaxSize().padding(padding)) {
                when (currentScreen) {
                    "DashboardScreen" -> DashboardScreen(onNavigate = navigateTo)
                    "InUseRoomsScreen" -> InUseRoomsScreen(onNavigate = navigateTo)
                    "RoomStatusScreen" -> RoomStatusScreen(onNavigate = navigateTo)
                    "RoomSummaryScreen" -> RoomSummaryScreen(onNavigate = navigateTo)
                    "AdminDashboardScreen" -> AdminDashboardScreen(onNavigate = navigateTo)
                    "AdminMembersScreen" -> AdminMembersScreen(onNavigateBack = popBackStack)
                    "HotelProfileScreen" -> HotelProfileScreen(onBack = popBackStack)
                    "RoomManagementScreen" -> RoomManagementScreen(onNavigateBack = popBackStack, onNavigate = navigateTo)
                    "FloorPlanEditorScreen" -> FloorPlanEditorScreen(onNavigateBack = popBackStack)
                    "AdminMenuManagementScreen" -> AdminMenuManagementScreen(onNavigateBack = popBackStack)
                    "PricingManagementScreen" -> PricingManagementScreen(onNavigateBack = popBackStack)
                    "AdminSupplierManagementScreen" -> AdminSupplierManagementScreen(onNavigateBack = popBackStack)
                    "AdminAgencyManagementScreen" -> AdminAgencyManagementScreen(onNavigateBack = popBackStack, onNavigate = navigateTo)
                    "AgencyContractScreen" -> AgencyContractScreen(onNavigateBack = popBackStack)
                    "AdminDepartmentManagementScreen" -> AdminDepartmentManagementScreen(onNavigateBack = popBackStack)
                    "AdminStaffManagementScreen" -> AdminStaffManagementScreen(onNavigateBack = popBackStack)
                    "AdminBlacklistScreen" -> AdminBlacklistScreen(onNavigateBack = popBackStack)
                    "KbsSettingsScreen" -> KbsSettingsScreen(onNavigateBack = popBackStack)
                    "ReservationListScreen" -> ReservationListScreen(onNavigate = navigateTo)
                    "NewReservationCalendarScreen" -> NewReservationCalendarScreen(onNavigate = navigateTo)
                    "CreateReservationScreen" -> CreateReservationScreen(onNavigateBack = popBackStack)
                    "ReservationCalendarScreen" -> CalendarScreen(onNavigate = navigateTo)
                    "CheckOutListScreen" -> CheckOutListScreen(onNavigate = navigateTo)
                    "CheckInListScreen" -> CheckInListScreen(onNavigate = navigateTo)
                    "HousekeepingScreen" -> TaskListScreen(onNavigateBack = popBackStack, onNavigate = navigateTo)
                    "MaintenanceScreen" -> MaintenanceListScreen(onNavigateBack = popBackStack, onNavigate = navigateTo)
                    "StaffListScreen" -> StaffListScreen(onNavigateBack = popBackStack, onNavigate = navigateTo)
                    "ShiftPlannerScreen" -> ShiftPlanningScreen(onNavigateBack = popBackStack, onNavigate = navigateTo)
                    "CashRegisterScreen" -> CashRegisterScreen(onNavigateBack = popBackStack, onNavigate = navigateTo)
                    "ProfitReportScreen" -> ProfitReportScreen(onNavigateBack = popBackStack, onNavigate = navigateTo)
                    "CurrentAccountsScreen" -> CurrentAccountsScreen(onNavigateBack = popBackStack, onNavigate = navigateTo)
                    "RestaurantPosScreen" -> RestaurantPosScreen(onNavigateBack = popBackStack)
                    "InventoryScreen" -> InventoryScreen(onNavigateBack = popBackStack, onNavigate = navigateTo)
                    "ChannelManagerScreen" -> ChannelManagerScreen(
                        onNavigateBack = popBackStack,
                        onNavigateToAllotment = { navigateTo("AgencyAllotmentScreen") }
                    )
                    "AgencyAllotmentScreen" -> AgencyAllotmentScreen(onNavigateBack = popBackStack)
                    "StatisticsScreen" -> StatisticsScreen(onNavigateBack = popBackStack)
                    "ReportsScreen" -> StatisticsScreen(onNavigateBack = popBackStack)
                    "HelpScreen" -> HelpScreen()
                    "SubscriptionScreen" -> SubscriptionScreen(onNavigateBack = popBackStack)
                    else -> Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) { Text("Ekran: $currentScreen") }
                }

                if (showOnboarding) {
                    OnboardingDialog(
                        onDismiss = { showOnboarding = false },
                        onNavigateToHelp = {
                            showOnboarding = false
                            navigateTo("HelpScreen")
                        },
                        onNavigateToHotelProfile = {
                            showOnboarding = false
                            navigateTo("HotelProfileScreen")
                        },
                        onNavigateToRoomManagement = {
                            showOnboarding = false
                            navigateTo("RoomManagementScreen")
                        }
                    )
                }
            }
        }
    }
}

